import { type MouseEvent, type ReactNode } from "react";
import { useDroppable } from "@dnd-kit/core";
import { Dropdown } from "antd";
import { type Dayjs } from "dayjs";
import { Plus } from "lucide-react";

import { cn } from "@fitness-app/utils/src/styles/cn";

import { formatLongDay, toDayKey, type CalendarEventsProps } from "./calendarModel";
import { EventPopover } from "./EventPopover";

const BUTTON_RESET = "cursor-pointer border-0 bg-transparent p-0 font-[inherit]";

interface CalendarDayCellProps<T> extends Omit<CalendarEventsProps<T>, "eventsByDay" | "isDayDimmed"> {
  day: Dayjs;
  events: T[];
  header: ReactNode;
  // Ile wierszy zmieści komórka; nadmiar idzie do „+N więcej". Brak = wszystkie.
  maxEvents?: number;
  className?: string;
}

export const CalendarDayCell = <T,>({
  day,
  events,
  header,
  maxEvents,
  getEventKey,
  renderEvent,
  dayMenu,
  onDayClick,
  className,
}: CalendarDayCellProps<T>) => {
  const { setNodeRef, isOver } = useDroppable({ id: toDayKey(day), data: { day: toDayKey(day) } });

  const overflows = maxEvents !== undefined && events.length > maxEvents;
  const visibleEvents = overflows ? events.slice(0, maxEvents - 1) : events;
  const hiddenCount = events.length - visibleEvents.length;
  const menuItems = dayMenu?.(day);

  // Tylko pusta część komórki: klik w chip, „+", „+N więcej" albo w portal popovera to nie klik w dzień.
  const handleDayClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!onDayClick || !e.currentTarget.contains(target) || target.closest("[data-calendar-chip], button")) {
      return;
    }
    onDayClick(day);
  };

  const body = (
    <div
      className={cn("flex h-full min-w-0 flex-col gap-1 p-1.5", onDayClick && "cursor-pointer")}
      onClick={handleDayClick}
    >
      <div className="flex h-6 items-center justify-between gap-1 px-0.5">
        {header}
        {menuItems && (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <button
              type="button"
              aria-label="Akcje dnia"
              className={cn(
                BUTTON_RESET,
                "flex size-6 items-center justify-center rounded-md text-gray-400 opacity-0 transition",
                "hover:bg-gray-100 hover:text-gray-700 focus-visible:opacity-100 group-hover:opacity-100",
              )}
            >
              <Plus size={14} />
            </button>
          </Dropdown>
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        {visibleEvents.map((event, index) => (
          <div key={getEventKey(event, index)} className="min-w-0">
            {renderEvent(event, { draggable: true })}
          </div>
        ))}
        {hiddenCount > 0 && (
          <EventPopover
            trigger="click"
            placement="bottom"
            mouseEnterDelay={0}
            content={
              <div className="flex w-72 flex-col gap-2 p-3">
                <div className="px-0.5 text-[13px] font-semibold text-gray-900 first-letter:uppercase">
                  {formatLongDay(day)}
                </div>
                <div className="flex flex-col gap-0.5">
                  {events.map((event, index) => (
                    <div key={getEventKey(event, index)} className="min-w-0">
                      {renderEvent(event, { draggable: true, dragIdScope: "overflow" })}
                    </div>
                  ))}
                </div>
              </div>
            }
          >
            <button
              type="button"
              className={cn(
                BUTTON_RESET,
                "h-6 rounded-md px-1.5 text-left text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-900",
              )}
            >
              +{hiddenCount} więcej
            </button>
          </EventPopover>
        )}
      </div>
    </div>
  );

  return (
    <div
      ref={setNodeRef}
      className={cn("group relative min-w-0 transition-colors", isOver && "bg-blue-50/80", className)}
    >
      {isOver && <div className="pointer-events-none absolute inset-0 z-10 ring-2 ring-inset ring-blue-400" />}
      {menuItems ? (
        <Dropdown menu={{ items: menuItems }} trigger={["contextMenu"]}>
          {body}
        </Dropdown>
      ) : (
        body
      )}
    </div>
  );
};
